import React, { useEffect, useMemo, useState } from "react";
import { CloseCircleOutlined } from "@ant-design/icons";

const groupMandatoryErrors = (
  errors = [],
  sections = [],
  subsectionsBySection = {},
) => {
  const groups = new Map();

  errors.forEach((entry) => {
    const sectionId = entry.sectionId || entry.sectionTitle || "unknown";
    const subSectionId =
      entry.subSectionId || entry.subsectionTitle || "unknown";
    const key = `${sectionId}:::${subSectionId}`;

    if (!groups.has(key)) {
      const sectionMatch = sections.find(
        (section) => section.sectionId === sectionId,
      );
      const subsectionList = subsectionsBySection[sectionId] || [];
      const subsectionMatch = subsectionList.find(
        (subsection) => subsection.id === subSectionId,
      );

      groups.set(key, {
        key,
        sectionId,
        sectionTitle: sectionMatch?.title || entry.sectionTitle || sectionId,
        subSectionId,
        subsectionTitle:
          subsectionMatch?.name ||
          subsectionMatch?.title ||
          entry.subsectionTitle ||
          subSectionId,
        questions: [],
      });
    }

    const questionLabel = entry.label || entry.questionId || "Mandatory field";
    const questionId = entry.questionId || questionLabel;
    const group = groups.get(key);
    const alreadyListed = group.questions.some(
      (question) => question.questionId === questionId,
    );

    if (!alreadyListed) {
      group.questions.push({
        questionId,
        label: questionLabel,
      });
    }
  });

  return [...groups.values()];
};

const MandatoryErrorsModal = ({
  open,
  message,
  errors = [],
  sections = [],
  subsectionsBySection = {},
  onClose,
}) => {
  const groupedErrors = useMemo(
    () => groupMandatoryErrors(errors, sections, subsectionsBySection),
    [errors, sections, subsectionsBySection],
  );

  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => {
    if (!open) {
      setOpenGroups({});
      return;
    }

    const initial = {};
    groupedErrors.forEach((group) => {
      initial[group.key] = true;
    });
    setOpenGroups(initial);
  }, [open, groupedErrors]);

  if (!open) return null;

  const toggleGroup = (key) => {
    setOpenGroups((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-lg rounded-md shadow-lg overflow-hidden bg-white font-sans max-h-[85vh] flex flex-col">
        <div className="py-3 px-4 bg-[#D32F2F] text-white font-semibold flex items-center gap-2">
          <CloseCircleOutlined />
          <span>Mandatory fields missing</span>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          <p className="text-base font-semibold text-[#06513D] mb-4">
            {message || "Please fill all mandatory fields before submitting."}
          </p>

          {groupedErrors.length === 0 ? (
            <p className="text-sm text-gray-600">
              Some mandatory fields are incomplete. Please review the form and
              try again.
            </p>
          ) : (
            <div className="space-y-3">
              {groupedErrors.map((group) => {
                const isExpanded = !!openGroups[group.key];

                return (
                  <div
                    key={group.key}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.key)}
                      className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
                        isExpanded ? "bg-teal-50" : "bg-white hover:bg-gray-50"
                      }`}
                    >
                      <div>
                        <p className="font-semibold text-gray-800">
                          Section: {group.sectionTitle}
                        </p>
                        <p className="text-sm text-gray-500">
                          Subsection: {group.subsectionTitle}
                        </p>
                      </div>
                      <svg
                        className={`w-5 h-5 text-gray-500 transition-transform shrink-0 ${
                          isExpanded ? "rotate-180" : ""
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    {isExpanded && (
                      <ul className="px-4 py-3 border-t border-gray-100 bg-white space-y-2">
                        {group.questions.map((question) => (
                          <li
                            key={`${group.key}-${question.questionId}`}
                            className="flex items-start gap-2 text-sm text-gray-700"
                          >
                            <span className="text-red-500 mt-0.5">•</span>
                            <span>{question.label}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-100 flex justify-center">
          <button
            type="button"
            onClick={onClose}
            className="w-32 px-4 py-2 rounded bg-[#06513D] text-white font-medium hover:bg-[#054a36]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default MandatoryErrorsModal;
